import React from 'react';
import { ReportPagination } from '@/components/shared/reports/ReportPagination';
import { FileText, Loader2 } from 'lucide-react';
import type { PostingLogTimesheet, TimesheetSortKey, SortDir } from '@/utils/PostingLog/types';
import { TimesheetTable } from './TimesheetTable';

interface ResultsTableProps {
  data: PostingLogTimesheet[];
  totalCount: number;
  isLoading: boolean;
  onTimesheetClick: (timesheetId: string) => void;
  sortKey: TimesheetSortKey;
  sortDir: SortDir;
  onSort: (key: TimesheetSortKey) => void;
  page: number;
  onPageChange: (p: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  columnVisibility?: Record<string, boolean>;
}

export const ResultsTable: React.FC<ResultsTableProps> = ({
  data, totalCount, isLoading, onTimesheetClick,
  sortKey, sortDir, onSort, page, onPageChange, pageSize, onPageSizeChange, columnVisibility,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
        {isLoading ? (
          <Loader2 className="h-10 w-10 opacity-30 animate-spin" />
        ) : (
          <>
            <FileText className="h-10 w-10 opacity-30" />
            <p className="text-sm">No posting records match your filters.</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <TimesheetTable
        timesheets={data}
        onTimesheetClick={onTimesheetClick}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
        columnVisibility={columnVisibility}
      />

      {/* Pagination */}
      <ReportPagination
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <p className="text-xs text-muted-foreground text-right">
        Showing{' '}
        {Math.min((page - 1) * pageSize + 1, totalCount)}–{Math.min((page - 1) * pageSize + data.length, totalCount)}{' '}
        of {totalCount} timesheet{totalCount !== 1 ? 's' : ''}
      </p>
    </div>
  );
};
